import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/auth";

/**
 * LogoutButton — fire-and-forget: tells the backend to invalidate the Redis
 * session + clear the cookie, flips local auth state to anonymous, then
 * navigates home. Intentionally NOT a full-page reload — we want the SPA to
 * re-render its public chrome (LoginButton in the CTA slot) in place.
 */
export interface LogoutButtonProps {
  className?: string;
  /** Path to land on after logout. Defaults to `/`. */
  redirectTo?: string;
}

export default function LogoutButton({ className, redirectTo = "/" }: LogoutButtonProps) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const classes =
    className ??
    "inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary";

  async function handleClick() {
    await logout();
    navigate(redirectTo, { replace: true });
  }

  return (
    <button type="button" onClick={handleClick} className={classes} data-testid="logout-button">
      {t("auth.signOut")}
    </button>
  );
}
